import React, {useState, useRef, useEffect, useCallback} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {KTSVG} from '../../../../../_metronic/helpers'
import clsx from 'clsx'
import styles from './ConversationItem.module.scss'

// Types
interface TeamMember {
  _id: string
  name: string
  email: string
  avatar?: string
  assignedContacts?: string[]
}

interface ContactField {
  name: string
  value: string
}

interface ConversationData {
  _id: string
  receiver: string
  message: string
  createdAt: string
  contacts: {
    _id: string
    isInboxRead: boolean
    isArchived: boolean
    isFlagged: boolean
    isTicked: boolean
  }
  contactgroupmaps: {
    _id: string
    fields: ContactField[]
    tags?: string[]
  }
}

interface ConversationItemProps {
  conversation: ConversationData
  isSelected: boolean
  activeConversationId?: string | null
  isBulkActionsActive?: boolean
  onSelect: (id: string, selected: boolean) => void
  onAssign: (id: string, assignee: TeamMember) => void
  onTag: (id: string) => void
  onUnsubscribe: (id: string) => void
  onArchive: (id: string) => void
  onClick: () => void
  onFlag: (id: string) => void
  onMarkAsUnread: (id: string) => void
  teamMembers: TeamMember[]
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  activeConversationId,
  isBulkActionsActive = false,
  onSelect,
  onAssign,
  onTag,
  onUnsubscribe,
  onArchive,
  onClick,
  onFlag,
  onMarkAsUnread,
  teamMembers,
}) => {
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Check if conversation is unread
  const isUnread = conversation.contacts && 'isInboxRead' in conversation.contacts && !conversation.contacts.isInboxRead

  // Get display name from contact fields - memoized for performance
  const displayName = React.useMemo(() => {
    const fields = conversation.contactgroupmaps?.fields || []
    const firstName = fields.find((field) => field.name === 'First Name')?.value || ''
    const lastName = fields.find((field) => field.name === 'Last Name')?.value || ''
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : conversation.receiver
  }, [conversation.contactgroupmaps?.fields, conversation.receiver])

  // Get initials for avatar - memoized for performance
  const initials = React.useMemo(() => {
    if (displayName === conversation.receiver) {
      // Use last two digits of phone number
      const digits = conversation.receiver.split('')
      return `${digits[digits.length - 2] || ''}${digits[digits.length - 1] || ''}`
    }
    return displayName.charAt(0).toUpperCase()
  }, [displayName, conversation.receiver])

  // Handle checkbox change
  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    onSelect(conversation.contacts._id, e.target.checked)
  }, [onSelect, conversation.contacts._id])

  // Handle actions menu toggle
  const handleActionsToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setShowActionsMenu(prev => !prev)
  }, [])

  // Handle assignment
  const handleAssign = useCallback((teamMember: TeamMember) => {
    onAssign(conversation.contacts._id, teamMember)
    setShowActionsMenu(false)
  }, [onAssign, conversation.contacts._id])

  // Handle individual actions
  const handleAction = useCallback((action: string) => {
    const contactId = conversation.contacts._id
    switch (action) {
      case 'tag':
        onTag(contactId)
        break
      case 'unsubscribe':
        onUnsubscribe(contactId)
        break
      case 'archive':
        onArchive(contactId)
        break
      case 'flag':
        onFlag(contactId)
        break
      case 'markAsUnread':
        onMarkAsUnread(contactId)
        break
    }
    setShowActionsMenu(false)
  }, [onTag, onUnsubscribe, onArchive, onFlag, onMarkAsUnread, conversation.contacts._id])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false)
      }
    }

    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showActionsMenu])


  // Tooltip components for action buttons
  const actionTooltip = (text: string) => (
    <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s+/g, '-')}`}>
      {text}
    </Tooltip>
  )
  return (
    <div
      className={clsx(
        'd-flex align-items-center p-3 cursor-pointer',
        styles.conversationItem,
        {
          [styles.unread]: isUnread,
          [styles.selected]: isSelected,
          [styles.active]: conversation.contacts && '_id' in conversation.contacts && conversation.contacts._id === activeConversationId,
        }
      )}
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-label={`Conversation with ${displayName}`}
    >
      {/* Checkbox for selection with tooltip */}
      <div className='d-flex align-items-center me-3'>
        <OverlayTrigger
          placement="top"
          overlay={actionTooltip(isSelected ? 'Deselect' : 'Select')}
        >
          <div className={clsx('form-check form-check-custom form-check-solid', styles.checkboxContainer)}>
            <input
              type='checkbox'
              className='form-check-input'
              checked={isSelected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              aria-label={isSelected ? 'Deselect conversation' : 'Select conversation'}
            />
          </div>
        </OverlayTrigger>
      </div>

      {/* Main conversation content */}
      <div
        className='d-flex align-items-center flex-grow-1'
        role="button"
        tabIndex={-1}
      >
        <div className='symbol symbol-45px symbol-circle'>
          <span
            className='symbol-label fs-6 fw-bolder'
            style={{backgroundColor: '#F4F4F4', color: '#404253'}}
          >
            {initials}
          </span>
          {isUnread && <span className='red-dot' />}
        </div>

        <div className={clsx('ms-5', styles.conversationContent)}>
          <div
            className={clsx(
              styles.conversationName,
              isUnread && styles.unreadName
            )}
          >
            {displayName}
          </div>
          <div
            className={clsx(
              styles.conversationNumber,
              isUnread && styles.unreadNumber
            )}
          >
            {conversation.receiver}
          </div>
        </div>
      </div>

      {/* Actions section */}
      <div className='d-flex flex-column align-items-end ms-2'>
        <div className={clsx('d-flex align-items-center', styles.actionsContainer)}>
          {/* Archive/Flag icons */}
          {conversation.contacts.isArchived ? (
            <OverlayTrigger
              placement='bottom'
              overlay={<Tooltip id='tooltip-unarchive'>Unarchive Message</Tooltip>}
            >
              <div
                className='py-4 px-1'
                onClick={(e) => {
                  e.stopPropagation()
                  handleAction('archive')
                }}
              >
                <i
                  className={clsx('m-2 svg-icon-3 svg-icon-gray-500 bi-archive-fill', styles.actionIcon)}
                  role="button"
                  tabIndex={0}
                  aria-label="Unarchive conversation"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      handleAction('archive')
                    }
                  }}
                />
              </div>
            </OverlayTrigger>
          ) : (
            <OverlayTrigger
              placement='bottom'
              overlay={<Tooltip id='tooltip-archive'>Archive Message</Tooltip>}
            >
              <div
                className='py-4 px-1'
                onClick={(e) => {
                  e.stopPropagation()
                  handleAction('archive')
                }}
              >
                <i
                  className={clsx('m-2 svg-icon-3 svg-icon-gray-400 bi-archive', styles.actionIcon)}
                  role="button"
                  tabIndex={0}
                  aria-label="Archive conversation"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      handleAction('archive')
                    }
                  }}
                />
              </div>
            </OverlayTrigger>
          )}

          <OverlayTrigger
            placement='bottom'
            overlay={
              <Tooltip id='tooltip-flag'>
                {conversation.contacts.isFlagged ? 'Unflag' : 'Flag'} Contact
              </Tooltip>
            }
          >
            <div
              className='py-4 px-1'
              onClick={(e) => {
                e.stopPropagation()
                handleAction('flag')
              }}
            >
              <i
                className={clsx(
                  'm-1 bi',
                  conversation.contacts.isFlagged ? 'bi-flag-fill text-danger' : 'bi-flag',
                  styles.actionIcon
                )}
                role="button"
                tabIndex={0}
                aria-label={`${conversation.contacts.isFlagged ? 'Unflag' : 'Flag'} conversation`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleAction('flag')
                  }
                }}
              />
            </div>
          </OverlayTrigger>

          {/* Actions button with tooltip - hidden when conversation is selected or bulk actions are active */}
          {!isSelected && !isBulkActionsActive && (
            <div className='position-relative' ref={menuRef}>
            <OverlayTrigger
              placement="top"
              overlay={actionTooltip('More actions')}
            >
              <button
                className={clsx('btn btn-sm btn-icon', styles.actionButton, {
                  'btn-light-primary': !showActionsMenu,
                  'btn-primary': showActionsMenu,
                })}
                onClick={handleActionsToggle}
                aria-label="More actions"
                aria-expanded={showActionsMenu}
                aria-haspopup="menu"
              >
                <i className='bi bi-three-dots fs-5'></i>
              </button>
            </OverlayTrigger>

            {/* Actions dropdown menu */}
            {showActionsMenu && (
              <div
                className={clsx('menu menu-sub menu-sub-dropdown show position-absolute', styles.actionsMenu)}
                style={{
                  top: '100%',
                  right: 0,
                  zIndex: 1050,
                  minWidth: '200px'
                }}
                role="menu"
              >
                <div className='menu-content'>
                  {/* Assign To submenu */}
                  <div className='menu-item'>
                    <div className='menu-content'>
                      <div className={clsx('text-muted text-uppercase fs-7 fw-bold', styles.menuHeading)}>
                        Assign To
                      </div>
                      {teamMembers.map((member) => (
                        <button
                          key={member._id}
                          className={clsx('d-flex align-items-center w-100', styles.menuItem, styles.teamMemberItem)}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAssign(member)
                          }}
                          role="menuitem"
                        >
                          <div className={clsx('symbol symbol-25px symbol-circle me-3', styles.teamMemberAvatar)}>
                            {member.avatar ? (
                              <img src={member.avatar} alt={member.name} className='symbol-label' />
                            ) : (
                              <span
                                className='symbol-label fs-7 fw-bolder'
                                style={{backgroundColor: '#F4F4F4', color: '#404253'}}
                              >
                                {member.name.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className='flex-grow-1 text-start'>
                            <div className='fw-bold fs-7'>{member.name}</div>
                            <div className='text-muted fs-8'>{member.email}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={clsx('separator my-2', styles.menuSeparator)}></div>

                  {/* Other actions */}
                  <OverlayTrigger
                    placement="left"
                    overlay={actionTooltip('Add tag to conversation')}
                  >
                    <button
                      className={clsx('d-flex align-items-center w-100', styles.menuItem)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAction('tag')
                      }}
                      role="menuitem"
                    >
                      <KTSVG path='/media/icons/duotune/communication/com010.svg' className='svg-icon-3 me-3' />
                      Add Tag
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="left"
                    overlay={actionTooltip('Mark conversation as unread')}
                  >
                    <button
                      className={clsx('d-flex align-items-center w-100', styles.menuItem)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAction('markAsUnread')
                      }}
                      role="menuitem"
                    >
                      <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-3 me-3' />
                      Mark as Unread
                    </button>
                  </OverlayTrigger>

                  <OverlayTrigger
                    placement="left"
                    overlay={actionTooltip('Unsubscribe from conversation')}
                  >
                    <button
                      className={clsx('d-flex align-items-center w-100', styles.menuItem)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAction('unsubscribe')
                      }}
                      role="menuitem"
                    >
                      <KTSVG path='/media/icons/duotune/general/gen040.svg' className='svg-icon-3 me-3' />
                      Unsubscribe
                    </button>
                  </OverlayTrigger>
                </div>
              </div>
            )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConversationItem
